import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Users, Wallet, ArrowLeft, CreditCard, Smartphone, Shield, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import ProtectionDepositCard from '@/components/ProtectionDepositCard';
import { fireConfettiBurst } from '@/components/ConfettiEffect';
import { ClubGuardianLeaderboard } from '@/components/GuardianLeaderboard';
import { PayPalPaymentButton } from '@/components/PayPalPayment';
import RotationCalendar from '@/components/RotationCalendar';
import RotationSetup from '@/components/RotationSetup';
import AutoContributionManager from '@/components/AutoContributionManager';
import ClubChat from '@/components/ClubChat';
import ClubYieldCard from '@/components/ClubYieldCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClubDetailPage() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [depositStatus, setDepositStatus] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showPayPal, setShowPayPal] = useState(false);

  useEffect(() => {
    loadClub();
    loadCurrentUser();
    
    // Check for deposit session
    const depositSessionId = searchParams.get('deposit_session');
    if (depositSessionId) {
      checkDepositStatus(depositSessionId);
    }
  }, [clubId, searchParams]);

  const loadCurrentUser = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Load user error:', error);
    }
  };

  const loadClub = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/clubs/${clubId}`, {
        withCredentials: true
      });
      setClub(response.data);
      setLoading(false);
      loadDepositStatus();
    } catch (error) {
      console.error('Load club error:', error);
      toast.error('Failed to load club details');
      setLoading(false);
    }
  };

  const loadDepositStatus = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/protection-deposits/${clubId}`,
        { withCredentials: true }
      );
      setDepositStatus(response.data);
    } catch (error) {
      // User might not have any deposit yet
      setDepositStatus(null);
    }
  };

  const checkDepositStatus = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/protection-deposits/checkout/status/${sessionId}`,
          { withCredentials: true }
        );

        if (response.data.payment_status === 'paid') {
          toast.success('Protection deposit added successfully!');
          navigate(`/clubs/${clubId}`, { replace: true });
          loadDepositStatus();
          return;
        }

        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          toast.info('Deposit processing... Please refresh the page.');
        }
      } catch (error) {
        console.error('Deposit check error:', error);
      }
    };

    poll();
  };

  // Check if current user is already a member
  useEffect(() => {
    if (club && currentUser) {
      setIsMember(club.members.includes(currentUser.user_id));
    }
  }, [club, currentUser]);

  const handleJoinClub = async () => {
    setProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/clubs/${clubId}/join`,
        {},
        { withCredentials: true }
      );
      toast.success('Joined club successfully!');
      fireConfettiBurst(2);
      loadClub();
    } catch (error) {
      console.error('Join club error:', error);
      toast.error(error.response?.data?.detail || 'Failed to join club');
    } finally {
      setProcessing(false);
    }
  };

  const handleMakeContribution = async (paymentMethod) => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/checkout/session`,
        {
          club_id: clubId,
          payment_method: paymentMethod,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else if (response.data.message) {
        toast.info(response.data.message);
        setProcessing(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Payment failed');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (!club) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <p className="text-stone-600 mb-4">Club not found</p>
            <Button onClick={() => navigate('/clubs')} className="rounded-full">
              Back to Clubs
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <main className="max-w-4xl mx-auto px-6 py-8">
        <Button
          data-testid="back-to-clubs-button"
          variant="ghost"
          onClick={() => navigate('/clubs')}
          className="mb-6 rounded-full"
        >
          <ArrowLeft className="mr-2 w-4 h-4" />
          Back to Clubs
        </Button>

        <Card className="border-stone-200 mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-3xl mb-2" data-testid="club-name">{club.name}</CardTitle>
                <Badge
                  className={club.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-700'}
                >
                  {club.status}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-stone-600 mb-6" data-testid="club-description">{club.description}</p>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="text-sm text-stone-600 mb-1">Contribution</div>
                <div className="text-2xl font-bold text-stone-900" data-testid="club-contribution">
                  ${club.contribution_amount}
                </div>
                <div className="text-xs text-stone-500">{club.contribution_frequency}</div>
              </div>
              <div className="bg-amber-50 rounded-xl p-4">
                <div className="text-sm text-stone-600 mb-1">Total Pool</div>
                <div className="text-2xl font-bold text-amber-500" data-testid="club-total-pool">
                  ${club.total_pool.toFixed(2)}
                </div>
                <div className="text-xs text-stone-500">Total saved</div>
              </div>
              <div className="bg-indigo-50 rounded-xl p-4">
                <div className="text-sm text-stone-600 mb-1">Members</div>
                <div className="text-2xl font-bold text-indigo-600" data-testid="club-members-count">
                  {club.members.length}
                </div>
                <div className="text-xs text-stone-500">Active savers</div>
              </div>
            </div>

            <div className="flex gap-3">
              {isMember ? (
                <Button
                  data-testid="already-member-badge"
                  disabled
                  className="flex-1 rounded-xl bg-green-100 text-green-700 hover:bg-green-100 cursor-default"
                >
                  <Users className="mr-2 w-4 h-4" />
                  You're a Member
                </Button>
              ) : depositStatus?.is_sufficient ? (
                <Button
                  data-testid="join-club-button"
                  onClick={handleJoinClub}
                  disabled={processing}
                  className="flex-1 rounded-xl bg-indigo-600 hover:bg-indigo-700"
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Users className="mr-2 w-4 h-4" />
                      Join Club
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  data-testid="deposit-required-button"
                  disabled
                  className="flex-1 rounded-xl bg-amber-100 text-amber-700 hover:bg-amber-100 cursor-default"
                >
                  <Shield className="mr-2 w-4 h-4" />
                  Add Protection Deposit First
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Protection Deposit Card - Show only if not a member */}
        {!isMember && (
          <div className="mb-6">
            <ProtectionDepositCard
              clubId={clubId}
              clubName={club.name}
              contributionAmount={club.contribution_amount}
              onDepositComplete={loadDepositStatus}
            />
          </div>
        )}

        {/* Rotation Calendar Section */}
        {isMember && (
          <div className="mb-6">
            <ClubYieldCard clubId={clubId} />
          </div>
        )}
        {isMember && (
          <div className="mb-6">
            {club.rotation_schedule && club.rotation_schedule.length > 0 ? (
              <RotationCalendar 
                clubId={clubId}
                isCreator={currentUser?.user_id === club.created_by}
                onPayoutProcessed={loadClub}
              />
            ) : currentUser?.user_id === club.created_by ? (
              <RotationSetup
                clubId={clubId}
                clubName={club.name}
                membersCount={club.members?.length || 0}
                onSetupComplete={loadClub}
              />
            ) : (
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-indigo-600" />
                    Rotation Calendar
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-4">
                    <p className="text-stone-500">
                      Rotation schedule not yet set up. The club creator will configure this.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Guardian Leaderboard */}
        <div className="mb-6">
          <ClubGuardianLeaderboard clubId={clubId} clubName={club.name} />
        </div>

        {/* Auto-Contribution Manager */}
        {isMember && (
          <div className="mb-6">
            <AutoContributionManager
              clubId={clubId}
              clubName={club.name}
              clubAmount={club.contribution_amount}
              clubFrequency={club.payout_frequency || club.contribution_frequency}
            />
          </div>
        )}

        {/* Club Chat — members only */}
        {isMember && (
          <div className="mb-6">
            <ClubChat
              clubId={clubId}
              currentUser={currentUser}
              clubCreatorId={club.created_by}
              clubMembers={club.members || []}
            />
          </div>
        )}

        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle>Make a Contribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Button
                data-testid="pay-with-card-button"
                onClick={() => handleMakeContribution('stripe')}
                disabled={processing}
                className="w-full rounded-xl py-6 bg-indigo-600 hover:bg-indigo-700 justify-start"
              >
                <CreditCard className="mr-3 w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">Pay with Card</div>
                  <div className="text-xs opacity-90">Visa, Mastercard, Amex</div>
                </div>
              </Button>
              
              {/* PayPal Payment Option */}
              <Button
                data-testid="pay-with-paypal-button"
                onClick={() => setShowPayPal(!showPayPal)}
                disabled={processing}
                variant="outline"
                className="w-full rounded-xl py-6 border-blue-200 hover:bg-blue-50 justify-start"
              >
                <svg className="mr-3 w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.384a.774.774 0 0 1 .763-.645h6.162c2.04 0 3.636.53 4.717 1.573.99.958 1.401 2.336 1.222 4.098-.477 4.684-3.276 6.308-6.789 6.308h-1.34c-.52 0-.96.379-1.04.892l-.563 3.727z" fill="#003087"/>
                  <path d="M21.329 7.632c-.018.12-.039.24-.062.362-.867 4.452-3.83 5.99-7.615 5.99h-1.927c-.463 0-.855.337-.928.794l-.986 6.25-.28 1.773a.49.49 0 0 0 .484.566h3.397c.405 0 .75-.294.813-.692l.034-.174.644-4.085.041-.224a.82.82 0 0 1 .814-.693h.512c3.318 0 5.916-1.348 6.678-5.248.318-1.63.154-2.99-.688-3.948a3.25 3.25 0 0 0-.931-.671z" fill="#0070E0"/>
                </svg>
                <div className="text-left">
                  <div className="font-semibold text-blue-900">Pay with PayPal</div>
                  <div className="text-xs text-blue-600">Fast & secure checkout</div>
                </div>
              </Button>
              
              {/* PayPal Button Container */}
              {showPayPal && (
                <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                  <PayPalPaymentButton
                    amount={club.contribution_amount}
                    description={`Contribution to ${club.name}`}
                    paymentType="contribution"
                    clubId={clubId}
                    onSuccess={() => {
                      toast.success('Contribution successful!');
                      fireConfettiBurst();
                      setShowPayPal(false);
                      loadClub();
                    }}
                    onCancel={() => setShowPayPal(false)}
                  />
                </div>
              )}
              
              <Button
                data-testid="pay-with-momo-button"
                onClick={() => handleMakeContribution('momo')}
                disabled={processing}
                variant="outline"
                className="w-full rounded-xl py-6 border-stone-200 hover:bg-stone-50 justify-start"
              >
                <Smartphone className="mr-3 w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">Pay with Mobile Money</div>
                  <div className="text-xs text-stone-600">Coming soon</div>
                </div>
              </Button>
            </div>
            <div className="mt-4 p-4 bg-amber-50 rounded-xl">
              <div className="flex items-start">
                <Wallet className="w-5 h-5 text-amber-600 mr-3 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-amber-900">Payment Security</div>
                  <div className="text-xs text-amber-700 mt-1">
                    All payments are processed securely through Stripe and PayPal. Your payment information is never stored on our servers.
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}